// src/pages/Dashboard.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { apiGet, apiPost } from '../api';
import { isAuthed } from '../auth';
import Header from '../components/Header';
import Footer from '../components/Footer';
import '../styles/Dashboard.css';

// ---------- BUCKET-ONLY TIME ZONES ----------
// Canonical buckets we persist to the backend
const BUCKETS = [
  { key: 'eastern', label: 'Eastern (America/New_York)' },
  { key: 'central', label: 'Central (America/Chicago)' },
  { key: 'mountain', label: 'Mountain (America/Denver/Phoenix)' },
  { key: 'pacific', label: 'Pacific (America/Los_Angeles)' },
  { key: 'alaska', label: 'Alaska (America/Anchorage)' },
  { key: 'hawaii', label: 'Hawaii (Pacific/Honolulu)' },
];

// Legacy + IANA → bucket mapping (frontend mirror of backend)
const LEGACY_TO_BUCKET = { east: 'eastern', west: 'pacific' };
const IANA_TO_BUCKET = {
  // hawaii
  'Pacific/Honolulu': 'hawaii',
  'US/Hawaii': 'hawaii',
  // alaska
  'America/Anchorage': 'alaska',
  'America/Juneau': 'alaska',
  'America/Nome': 'alaska',
  'America/Sitka': 'alaska',
  'America/Yakutat': 'alaska',
  'US/Alaska': 'alaska',
  // pacific
  'America/Los_Angeles': 'pacific',
  'America/Tijuana': 'pacific',
  'US/Pacific': 'pacific',
  // mountain
  'America/Denver': 'mountain',
  'America/Boise': 'mountain',
  'America/Phoenix': 'mountain',
  'US/Mountain': 'mountain',
  // central
  'America/Chicago': 'central',
  'US/Central': 'central',
  // eastern
  'America/New_York': 'eastern',
  'US/Eastern': 'eastern',
};

const CANON_SET = new Set(['hawaii', 'alaska', 'pacific', 'mountain', 'central', 'eastern', 'test']);
const isBucket = (z) => typeof z === 'string' && CANON_SET.has(z);

// Normalize whatever the server has to one of our display buckets.
// - Keep 'test' only for dev accounts that already had it.
// - Map legacy 'east'/'west' and common IANA names to a bucket.
// - Otherwise, return '' (forces user to choose).
function normalizeToBucket(value, originalRaw) {
  if (!value) return originalRaw === 'test' ? 'test' : '';
  const v = String(value).trim();
  if (isBucket(v)) return v;
  if (LEGACY_TO_BUCKET[v]) return LEGACY_TO_BUCKET[v];
  if (IANA_TO_BUCKET[v]) return IANA_TO_BUCKET[v];
  return originalRaw === 'test' ? 'test' : '';
}

// ---------- existing helpers (unchanged) ----------
function ordinal(n) {
  const s = ['th', 'st', 'nd', 'rd'],
    v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}
function formatRenewalDate(val) {
  if (!val) return '—';
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.toLocaleString(undefined, { month: 'long' })} ${ordinal(d.getDate())}, ${d.getFullYear()}`;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [me, setMe] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);
  const [showDeveloperCard, setShowDeveloperCard] = useState(false);
  const [billingError, setBillingError] = useState('');
  const [debugInfo, setDebugInfo] = useState({});

  // Loading states for API buttons
  const [subscribing, setSubscribing] = useState(false);
  const [managingBilling, setManagingBilling] = useState(false);

  // Checkout success banner
  const [checkoutSuccess, setCheckoutSuccess] = useState(searchParams.get('checkout') === 'success');

  // Save state
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState('');
  const [saveError, setSaveError] = useState('');

  // Timezone auto-save notification
  const [tzAutoSaved, setTzAutoSaved] = useState('');

  // Copy debug info state
  const [debugCopied, setDebugCopied] = useState(false);

  // Support email toast state
  const [supportToast, setSupportToast] = useState(false);

  // Editable state
  const [dob, setDob] = useState('');
  const [tob, setTob] = useState('');
  const [tz, setTz] = useState('');
  const [originalTz, setOriginalTz] = useState('');

  // Time of birth input state (12-hour format for user input)
  const [tobHour, setTobHour] = useState('');
  const [tobMinute, setTobMinute] = useState('');
  const [tobPeriod, setTobPeriod] = useState('AM');

  // Convert 24-hour time (HH:mm) to 12-hour components
  const parse24HourTime = (time24) => {
    if (!time24 || typeof time24 !== 'string') return { hour: '', minute: '', period: 'AM' };
    const [hourStr, minuteStr] = time24.split(':');
    const hour24 = parseInt(hourStr, 10);
    const minute = minuteStr || '00';

    if (isNaN(hour24)) return { hour: '', minute, period: 'AM' };

    let hour12 = hour24 % 12;
    if (hour12 === 0) hour12 = 12;
    const period = hour24 >= 12 ? 'PM' : 'AM';

    return { hour: String(hour12), minute, period };
  };

  // Convert 12-hour components to 24-hour time (HH:mm)
  const format24HourTime = (hour, minute, period) => {
    const h = parseInt(hour, 10);
    const m = parseInt(minute, 10);

    if (isNaN(h) || isNaN(m) || h < 1 || h > 12 || m < 0 || m > 59) {
      return null;
    }

    let hour24 = h;
    if (period === 'PM' && h !== 12) hour24 += 12;
    if (period === 'AM' && h === 12) hour24 = 0;

    return `${String(hour24).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };

  // Auto-detect user's timezone and map to closest bucket
  const detectTimezoneBucket = React.useCallback(() => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      // console.log('[Timezone Detection] Browser timezone:', tz);

      // Map IANA timezone to bucket
      if (IANA_TO_BUCKET[tz]) {
        // console.log('[Timezone Detection] Mapped to bucket:', IANA_TO_BUCKET[tz]);
        return IANA_TO_BUCKET[tz];
      }

      // Fallback: use UTC offset to guess bucket
      const offset = -new Date().getTimezoneOffset() / 60; // Offset in hours (negative for west of UTC)
      // console.log('[Timezone Detection] No IANA match, using UTC offset:', offset);

      if (offset === -10) return 'hawaii';
      if (offset === -9) return 'alaska';
      if (offset === -8) return 'pacific';
      if (offset === -7) return 'mountain';
      if (offset === -6) return 'central';
      // Default to eastern for -5 or anything else
      // console.log('[Timezone Detection] Defaulting to eastern (offset:', offset, ')');
      return 'eastern';
    } catch (e) {
      // If anything fails, default to eastern
      // console.error('[Timezone Detection] Error:', e);
      // console.log('[Timezone Detection] Fallback to eastern');
      return 'eastern';
    }
  }, []);

  // Data fetching effect
  useEffect(() => {
    // Check auth first
    const idToken = localStorage.getItem('idToken');
    const apiBase = process.env.REACT_APP_API_BASE;

    // Capture initial debug info
    const initialDebug = {
      timestamp: new Date().toISOString(),
      hasIdToken: !!idToken,
      idTokenLength: idToken?.length || 0,
      idTokenPrefix: idToken?.substring(0, 20) + '...' || 'none',
      apiBase: apiBase || 'not set',
      currentUrl: window.location.href,
      userAgent: navigator.userAgent,
      isAuthed: isAuthed(),
    };

    // console.log('[Dashboard] Initial debug info:', initialDebug);
    setDebugInfo(initialDebug);

    if (!isAuthed()) {
      navigate('/login', { replace: true });
      return;
    }

    let mounted = true;
    const startTime = Date.now();

    // Capture request info
    const requestUrl = `${apiBase}/me`;
    // console.log('[Dashboard] Making API request to:', requestUrl);

    setDebugInfo((prev) => ({ ...prev, requestUrl, requestStartTime: new Date().toISOString() }));

    apiGet('/me')
      .then(async (data) => {
        if (!mounted) return;

        // console.log('[Dashboard] API response received:', data);
        setDebugInfo((prev) => ({
          ...prev,
          responseReceived: new Date().toISOString(),
          responseData: data,
          responseSuccess: true,
        }));

        // Validate response data
        if (!data || typeof data !== 'object') {
          // console.error('[Dashboard] Invalid response data:', data);
          throw new Error('Invalid response from server');
        }

        setMe(data);
        setDob(data.birth_date || '');
        const birthTime = (data.birth_time || '').slice(0, 5);
        setTob(birthTime);
        // Parse time into 12-hour format components
        const { hour, minute, period } = parse24HourTime(birthTime);
        setTobHour(hour);
        setTobMinute(minute);
        setTobPeriod(period);
        const rawTz = data.timezone || '';
        setOriginalTz(rawTz);

        // Auto-detect timezone (always log it)
        const detected = detectTimezoneBucket();

        if (!rawTz) {
          setTz(detected);
          // Auto-save the detected timezone
          try {
            await apiPost('/me/update', { timezone: detected });
            setOriginalTz(detected);
            // Show notification
            setTzAutoSaved(detected);
            setTimeout(() => setTzAutoSaved(''), 8000);
          } catch (e) {
            // console.error('[Dashboard] Failed to auto-save timezone:', e);
            setDebugInfo((prev) => ({ ...prev, timezoneAutoSaveError: e?.message }));
            // Don't fail the whole dashboard if timezone auto-save fails
          }
        } else {
          setTz(normalizeToBucket(rawTz, rawTz));
        }
      })
      .catch((e) => {
        if (!mounted) return;

        // console.error('[Dashboard] Error loading profile:', e);

        // Capture error details
        const errorDetails = {
          errorMessage: e?.message || e?.toString() || 'Unknown error',
          errorType: e?.constructor?.name || 'Error',
          errorStack: e?.stack,
          responseStatus: e?.status,
          responseFailed: new Date().toISOString(),
          isCORSError: e?.message?.includes('CORS') || e?.message?.includes('Failed to fetch'),
          isNetworkError: e?.message?.includes('Network error') || e?.message?.includes('Load failed'),
        };

        // console.error('[Dashboard] Error details:', errorDetails);
        setDebugInfo((prev) => ({ ...prev, ...errorDetails, responseSuccess: false }));

        // If 401, tokens already cleared by apiGet - just redirect
        if (e?.message === 'UNAUTHORIZED') {
          navigate('/login', { replace: true });
          return;
        }

        // More detailed error messages for debugging
        let errorMessage = e?.message || e?.toString() || 'Failed to load profile';

        // Provide helpful context for CORS errors
        if (errorDetails.isCORSError) {
          errorMessage = `API connection blocked (likely CORS). The API at ${process.env.REACT_APP_API_BASE} may not allow requests from ${window.location.origin}. Check browser console for details.`;
        }

        // console.error('[Dashboard] Setting error:', errorMessage);
        setErr(errorMessage);
      })
      .finally(() => {
        if (!mounted) return;
        const elapsed = Date.now() - startTime;
        const delay = Math.max(0, 1000 - elapsed); // Ensure at least 1s total
        setTimeout(() => {
          if (mounted) setLoading(false);
        }, delay);
      });
    return () => {
      mounted = false;
    };
  }, [navigate, detectTimezoneBucket]);

  const plan = useMemo(() => (me?.plan ? me.plan.charAt(0).toUpperCase() + me.plan.slice(1) : 'None'), [me]);
  const renewalDate = useMemo(() => (me?.end_date ? formatRenewalDate(me.end_date) : '—'), [me]);

  async function handleSubscribe() {
    setBillingError('');
    setSubscribing(true);
    try {
      const res = await apiPost('/billing/checkout/session', { plan: 'basic' });
      const url = res?.url;
      if (!url) throw new Error(res?.error || 'No checkout URL returned');
      window.location.assign(url);
      // Note: setSubscribing(false) not needed since we're navigating away
    } catch (e) {
      if (e?.message === 'UNAUTHORIZED') {
        navigate('/login', { replace: true });
        return;
      }
      setBillingError(e?.message || 'Failed to start checkout');
      setTimeout(() => setBillingError(''), 6000);
      setSubscribing(false);
    }
  }

  async function handleManageBilling() {
    // Clear any previous error
    setBillingError('');

    // Check if user has ever subscribed
    if (!me?.stripe_customer_id) {
      setBillingError('Please subscribe to a plan first. You can manage billing after your first subscription.');
      // Auto-hide after 6 seconds
      setTimeout(() => setBillingError(''), 6000);
      return;
    }

    setManagingBilling(true);
    try {
      const { url } = await apiPost('/billing/portal', {});
      if (!url) throw new Error('No portal URL returned');
      window.location.assign(url);
      // Note: setManagingBilling(false) not needed since we're navigating away
    } catch (e) {
      if (e?.message === 'UNAUTHORIZED') {
        navigate('/login', { replace: true });
        return;
      }
      setBillingError(e?.message || 'Could not open Billing Portal');
      setTimeout(() => setBillingError(''), 6000);
      setManagingBilling(false);
    }
  }

  async function refetchMe() {
    const data = await apiGet('/me');
    setMe(data);
    setDob(data.birth_date || '');
    const birthTime = (data.birth_time || '').slice(0, 5);
    setTob(birthTime);
    // Parse time into 12-hour format components
    const { hour, minute, period } = parse24HourTime(birthTime);
    setTobHour(hour);
    setTobMinute(minute);
    setTobPeriod(period);
    const rawTz = data.timezone || '';
    setOriginalTz(rawTz);
    setTz(normalizeToBucket(rawTz, rawTz));
  }

  async function handleSave() {
    setSaving(true);
    setSaveError('');
    setSaveSuccess('');

    try {
      // Convert 12-hour time input to 24-hour format
      let birthTime = null;
      if (tobHour && tobMinute) {
        birthTime = format24HourTime(tobHour, tobMinute, tobPeriod);
        if (!birthTime) {
          setSaveError('Invalid time format. Please enter valid hour (1-12) and minute (00-59).');
          setSaving(false);
          setTimeout(() => setSaveError(''), 6000);
          return;
        }
      }

      const payload = {
        birth_date: dob || null,
        birth_time: birthTime,
      };
      // Only send canonical buckets (or 'test' if this account already had it)
      if (isBucket(tz)) payload.timezone = tz;

      await apiPost('/me/update', payload);
      await refetchMe();
      setSaveSuccess('Your preferences were saved successfully.');
      setTimeout(() => setSaveSuccess(''), 6000);
    } catch (e) {
      if (e?.message === 'UNAUTHORIZED') {
        navigate('/login', { replace: true });
        return;
      }
      setSaveError(e?.message || 'Could not save. Please try again.');
      setTimeout(() => setSaveError(''), 6000);
    } finally {
      setSaving(false);
    }
  }

  // Helper: Check if hostname is a local/private IP
  function getNetworkType(hostname) {
    if (hostname === 'localhost' || hostname === '127.0.0.1') return 'localhost';
    // Check for private IP ranges
    const ipMatch = hostname.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
    if (ipMatch) {
      const [, a, b] = ipMatch.map(Number);
      if (a === 10) return 'private-10.x (Class A)';
      if (a === 172 && b >= 16 && b <= 31) return 'private-172.16-31.x (Class B)';
      if (a === 192 && b === 168) return 'private-192.168.x (Class C)';
      return 'public-ip';
    }
    return 'domain';
  }

  // Helper: Get JWT expiration info
  function getTokenExpirationInfo() {
    try {
      const token = localStorage.getItem('idToken');
      if (!token) return { hasToken: false };
      const parts = token.split('.');
      if (parts.length !== 3) return { hasToken: true, valid: false };
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      const expSeconds = payload.exp;
      const iatSeconds = payload.iat;
      const nowSeconds = Math.floor(Date.now() / 1000);
      return {
        hasToken: true,
        valid: true,
        issuedAt: iatSeconds ? new Date(iatSeconds * 1000).toISOString() : 'unknown',
        expiresAt: expSeconds ? new Date(expSeconds * 1000).toISOString() : 'unknown',
        secondsRemaining: expSeconds ? Math.max(0, expSeconds - nowSeconds) : 0,
        isExpired: expSeconds ? expSeconds < nowSeconds : false,
        tokenAgeSeconds: iatSeconds ? nowSeconds - iatSeconds : 0,
      };
    } catch (e) {
      return { hasToken: true, valid: false, parseError: e.message };
    }
  }

  // Helper: Get localStorage info
  function getLocalStorageInfo() {
    try {
      const keys = Object.keys(localStorage);
      let totalSize = 0;
      keys.forEach((key) => {
        totalSize += (localStorage.getItem(key) || '').length * 2; // UTF-16
      });
      return {
        itemCount: keys.length,
        estimatedSizeBytes: totalSize,
        estimatedSizeKB: (totalSize / 1024).toFixed(2),
        hasIdToken: keys.includes('idToken'),
        hasAccessToken: keys.includes('accessToken'),
        hasRefreshToken: keys.includes('refreshToken'),
      };
    } catch (e) {
      return { error: e.message };
    }
  }

  // Helper: Get performance metrics
  function getPerformanceInfo() {
    const info = {};
    try {
      if (performance.timing) {
        const t = performance.timing;
        info.pageLoadTime = t.loadEventEnd - t.navigationStart;
        info.domContentLoaded = t.domContentLoadedEventEnd - t.navigationStart;
        info.dnsLookup = t.domainLookupEnd - t.domainLookupStart;
        info.tcpConnect = t.connectEnd - t.connectStart;
        info.serverResponse = t.responseEnd - t.requestStart;
      }
      if (performance.memory) {
        info.jsHeapSizeLimit = Math.round(performance.memory.jsHeapSizeLimit / 1024 / 1024) + 'MB';
        info.totalJSHeapSize = Math.round(performance.memory.totalJSHeapSize / 1024 / 1024) + 'MB';
        info.usedJSHeapSize = Math.round(performance.memory.usedJSHeapSize / 1024 / 1024) + 'MB';
      }
      info.pageOpenDuration = Math.round((Date.now() - performance.timeOrigin) / 1000) + 's';
    } catch (e) {
      info.error = e.message;
    }
    return info;
  }

  // Copy debug info to clipboard
  async function handleCopyDebugInfo() {
    const hostname = window.location.hostname;
    const networkType = getNetworkType(hostname);
    const tokenInfo = getTokenExpirationInfo();
    const storageInfo = getLocalStorageInfo();
    const perfInfo = getPerformanceInfo();

    // Create comprehensive debug info including CORS-related details
    const fullDebugInfo = {
      ...debugInfo,
      // Network/CORS info
      networkInfo: {
        origin: window.location.origin,
        hostname: hostname,
        port: window.location.port || 'default',
        protocol: window.location.protocol,
        networkType: networkType,
        isLocalDev: networkType === 'localhost' || networkType.startsWith('private-'),
        isPrivateNetwork: networkType.startsWith('private-'),
        apiBaseUrl: process.env.REACT_APP_API_BASE,
      },
      // Browser/Device info
      browserInfo: {
        userAgent: navigator.userAgent,
        language: navigator.language,
        languages: navigator.languages?.join(', '),
        platform: navigator.platform,
        vendor: navigator.vendor,
        cookiesEnabled: navigator.cookieEnabled,
        onLine: navigator.onLine,
        doNotTrack: navigator.doNotTrack,
        hardwareConcurrency: navigator.hardwareConcurrency,
        deviceMemory: navigator.deviceMemory ? navigator.deviceMemory + 'GB' : 'unknown',
        maxTouchPoints: navigator.maxTouchPoints,
        isTouchDevice: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
      },
      // Screen info
      screenInfo: {
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        availWidth: window.screen.availWidth,
        availHeight: window.screen.availHeight,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
        devicePixelRatio: window.devicePixelRatio,
        colorDepth: window.screen.colorDepth,
        orientation: window.screen.orientation?.type || 'unknown',
      },
      // Connection info (if available)
      connectionInfo: navigator.connection ? {
        effectiveType: navigator.connection.effectiveType,
        downlink: navigator.connection.downlink + 'Mbps',
        rtt: navigator.connection.rtt + 'ms',
        saveData: navigator.connection.saveData,
      } : { available: false },
      // Token expiration info
      tokenInfo: tokenInfo,
      // LocalStorage info
      storageInfo: storageInfo,
      // Performance metrics
      performanceInfo: perfInfo,
      // Feature detection
      featureSupport: {
        serviceWorker: 'serviceWorker' in navigator,
        webGL: (() => { try { return !!document.createElement('canvas').getContext('webgl'); } catch (e) { return false; } })(),
        webSocket: 'WebSocket' in window,
        indexedDB: 'indexedDB' in window,
        localStorage: 'localStorage' in window,
        sessionStorage: 'sessionStorage' in window,
        crypto: 'crypto' in window,
        clipboard: !!navigator.clipboard,
        geolocation: 'geolocation' in navigator,
        notifications: 'Notification' in window,
        bluetooth: 'bluetooth' in navigator,
      },
      // Environment info
      environment: {
        nodeEnv: process.env.NODE_ENV,
        reactAppApiBase: process.env.REACT_APP_API_BASE,
        reactAppRegion: process.env.REACT_APP_AWS_REGION,
        buildTime: process.env.REACT_APP_BUILD_TIME || 'not set',
      },
      // Timestamp
      debugTimestamp: new Date().toISOString(),
      debugTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      // Profile data if available
      profileData: me,
    };

    const debugText = JSON.stringify(fullDebugInfo, null, 2);

    // Try modern Clipboard API first (requires HTTPS)
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(debugText);
        // console.log('[Dashboard] Debug info copied via Clipboard API');
        setDebugCopied(true);
        setTimeout(() => setDebugCopied(false), 3000);
        return;
      } catch (err) {
        // console.warn('[Dashboard] Clipboard API failed, trying fallback:', err);
      }
    }

    // Fallback: Use deprecated execCommand (works over HTTP)
    try {
      // Create temporary textarea
      const textarea = document.createElement('textarea');
      textarea.value = debugText;
      // Position off-screen
      textarea.style.position = 'fixed';
      textarea.style.top = '-9999px';
      textarea.style.left = '-9999px';
      textarea.style.opacity = '0';
      textarea.setAttribute('readonly', '');
      document.body.appendChild(textarea);

      // Select and copy
      textarea.select();
      textarea.setSelectionRange(0, debugText.length);
      const successful = document.execCommand('copy');

      // Clean up
      document.body.removeChild(textarea);

      if (successful) {
        // console.log('[Dashboard] Debug info copied via execCommand fallback');
        setDebugCopied(true);
        setTimeout(() => setDebugCopied(false), 3000);
      } else {
        throw new Error('execCommand returned false');
      }
    } catch (err) {
      // console.error('[Dashboard] All copy methods failed:', err);
      // Last resort: show in an alert (user can manually copy)
      alert('Unable to copy automatically. Debug info:\n\n' + debugText.substring(0, 500) + '...');
      // console.log('Full debug info:', debugText);
    }
  }

  // Send debug info to support via email
  function handleSendToSupport() {
    const phone = me?.phone_number || me?.PK || 'unknown';
    const hostname = window.location.hostname;
    const networkType = getNetworkType(hostname);
    const tokenInfo = getTokenExpirationInfo();

    // Detect CORS issue
    const isCORSError =
      debugInfo.isCORSError ||
      debugInfo.errorMessage?.includes('CORS') ||
      debugInfo.errorMessage?.includes('Failed to fetch') ||
      debugInfo.errorMessage?.includes('Network error') ||
      debugInfo.errorMessage?.includes('Load failed');

    // Build comprehensive debug info
    const lines = [
      `=== USER PROFILE ===`,
      `Phone: ${phone}`,
      `Plan: ${me?.plan || 'None'}`,
      `Subscription Status: ${me?.subscription_status || 'N/A'}`,
      `Stripe Customer ID: ${me?.stripe_customer_id || 'None'}`,
      `Timezone: ${me?.timezone || 'Not set'}`,
      `Birth Date: ${me?.birth_date || 'Not set'}`,
      ``,
      `=== AUTH STATE ===`,
      `ID Token: ${debugInfo.hasIdToken ? `Present (${debugInfo.idTokenLength} chars)` : 'MISSING'}`,
      `Token Expired: ${tokenInfo.isExpired ? 'YES' : 'No'}`,
      `Token Expires: ${tokenInfo.expiresAt || 'unknown'}`,
      `Time Remaining: ${tokenInfo.secondsRemaining ? Math.round(tokenInfo.secondsRemaining / 60) + ' min' : 'N/A'}`,
      `isAuthed(): ${debugInfo.isAuthed}`,
      ``,
      `=== NETWORK INFO ===`,
      `Network Type: ${networkType}`,
      `Is Local Dev: ${networkType === 'localhost' || networkType.startsWith('private-') ? 'YES' : 'No'}`,
      `Origin: ${window.location.origin}`,
      `Protocol: ${window.location.protocol}`,
      `Host: ${window.location.host}`,
      `Online: ${navigator.onLine ? 'Yes' : 'NO - OFFLINE'}`,
      ``,
      `=== API REQUEST ===`,
      `API Base: ${debugInfo.apiBase || 'NOT SET'}`,
      `Request URL: ${debugInfo.requestUrl || 'N/A'}`,
      `Response: ${debugInfo.responseSuccess === true ? 'Success' : debugInfo.responseSuccess === false ? 'FAILED' : 'Pending'}`,
      debugInfo.errorMessage ? `Error: ${debugInfo.errorMessage}` : null,
      debugInfo.errorType ? `Error Type: ${debugInfo.errorType}` : null,
      ``,
      `=== CORS DIAGNOSIS ===`,
      `CORS Issue Detected: ${isCORSError ? 'YES - LIKELY CORS PROBLEM' : 'No'}`,
      `API Target: ${process.env.REACT_APP_API_BASE}`,
      ``,
      `=== BROWSER/DEVICE ===`,
      `Browser: ${navigator.userAgent}`,
      `Platform: ${navigator.platform}`,
      `Language: ${navigator.language}`,
      `Screen: ${window.screen.width}x${window.screen.height}`,
      `Viewport: ${window.innerWidth}x${window.innerHeight}`,
      `Pixel Ratio: ${window.devicePixelRatio}`,
      `Touch Device: ${'ontouchstart' in window || navigator.maxTouchPoints > 0 ? 'Yes' : 'No'}`,
      `Cookies Enabled: ${navigator.cookieEnabled ? 'Yes' : 'No'}`,
      ``,
      `=== ENVIRONMENT ===`,
      `Timestamp: ${new Date().toISOString()}`,
      `Browser Timezone: ${Intl.DateTimeFormat().resolvedOptions().timeZone}`,
      `Page: ${window.location.href}`,
      `Node Env: ${process.env.NODE_ENV}`,
    ];

    const summary = lines.filter(Boolean).join('\n');

    const subject = encodeURIComponent(
      isCORSError ? 'Support: DEBUG LOG DUMP [CORS ERROR]' : 'Support: DEBUG LOG DUMP'
    );
    const body = encodeURIComponent(summary);

    window.location.href = `mailto:help@yourhighestself.org?subject=${subject}&body=${body}`;

    // Show toast after 5 seconds (after user returns from mail app)
    setTimeout(() => {
      setSupportToast(true);
      setTimeout(() => setSupportToast(false), 5000);
    }, 5000);
  }

  // Build options: six buckets; include 'test' only if this account already had it.
  const tzOptions = useMemo(() => {
    const base = [...BUCKETS];
    if (originalTz === 'test' || tz === 'test') {
      base.unshift({ key: 'test', label: 'Test (dev only)' });
    }
    return base;
  }, [originalTz, tz]);

  // Clear checkout param after reading it
  useEffect(() => {
    if (searchParams.get('checkout')) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('checkout');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Check if account is being set up (new user, no timezone yet)
  const accountPending = me && !me.stripe_customer_id && !me.timezone;

  // Check if subscription is being set up (has subscribed but not fully active)
  const subscriptionPending = me && me.stripe_customer_id && me.subscription_status !== 'active';

  return (
    <main className="dashboard-fade-in">
      <Header />
      <div className="container page">
        {err && (
          <div className="card card--error">
            <div style={{ marginBottom: '0.75rem' }}>
              <strong>Error:</strong> {err}
            </div>
            {(err.includes('CORS') || err.includes('Network error')) && (
              <details style={{ fontSize: '0.85rem', opacity: 0.9, marginTop: '0.75rem' }}>
                <summary style={{ cursor: 'pointer', marginBottom: '0.5rem' }}>Need help?</summary>
                <ol style={{ paddingLeft: '1.25rem', margin: '0.5rem 0', lineHeight: 1.7 }}>
                  <li>First, try logging out and logging back in</li>
                  <li>If that doesn't help, scroll to the bottom of this page and tap <strong>"Developer"</strong> in the footer</li>
                  <li>Then tap <strong>"Send to Support"</strong> to email us your diagnostic info, or copy it and send to <strong>help@yourhighestself.org</strong></li>
                </ol>
              </details>
            )}
          </div>
        )}

        {/* Checkout success banner */}
        {checkoutSuccess && (
          <div
            style={{
              marginBottom: '1.5rem',
              padding: '16px 52px 16px 18px',
              background: 'rgba(34, 197, 94, 0.15)',
              border: '1px solid rgba(34, 197, 94, 0.4)',
              borderRadius: '12px',
              color: '#86efac',
              position: 'relative',
            }}
          >
            <button
              onClick={() => setCheckoutSuccess(false)}
              style={{
                position: 'absolute',
                top: 10,
                right: 12,
                width: 28,
                height: 28,
                background: 'transparent',
                border: 'none',
                color: '#86efac',
                fontSize: 20,
                cursor: 'pointer',
                borderRadius: 6,
              }}
              aria-label="Dismiss"
            >
              ×
            </button>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Welcome to Your Highest Self! ✨</div>
            <div style={{ fontSize: '0.9rem', opacity: 0.95 }}>Your subscription is active. You'll receive your first daily message tomorrow morning in your local timezone.</div>
          </div>
        )}

        {/* Timezone auto-save notification */}
        {tzAutoSaved && (
          <div
            style={{
              marginBottom: '1.5rem',
              padding: '14px 18px',
              background: 'rgba(59, 130, 246, 0.15)',
              border: '1px solid rgba(59, 130, 246, 0.4)',
              borderRadius: '12px',
              color: '#93c5fd',
              fontSize: '0.9rem',
            }}
          >
            Timezone automatically set to <b>{BUCKETS.find((b) => b.key === tzAutoSaved)?.label || tzAutoSaved}</b>
          </div>
        )}

        <header className="dash-head">
          <h1>Dashboard</h1>
        </header>

        <section className="card">
          <h2>Profile</h2>
          {loading ? (
            // Loading skeleton
            <div>
              <div
                style={{
                  height: 20,
                  background: 'rgba(255,255,255,0.1)',
                  borderRadius: 4,
                  marginBottom: 12,
                  width: '60%',
                  animation: 'pulse 1.5s ease-in-out infinite',
                }}
              />
              <div
                style={{
                  height: 20,
                  background: 'rgba(255,255,255,0.1)',
                  borderRadius: 4,
                  marginBottom: 12,
                  width: '40%',
                  animation: 'pulse 1.5s ease-in-out infinite',
                  animationDelay: '0.2s',
                }}
              />
              <div
                style={{
                  height: 20,
                  background: 'rgba(255,255,255,0.1)',
                  borderRadius: 4,
                  width: '50%',
                  animation: 'pulse 1.5s ease-in-out infinite',
                  animationDelay: '0.4s',
                }}
              />
              <style>{`
                @keyframes pulse {
                  0%, 100% { opacity: 1; }
                  50% { opacity: 0.5; }
                }
              `}</style>
            </div>
          ) : me ? (
            <>
              <ul className="list">
                <li>
                  <b>Phone:</b>{' '}
                  {(() => {
                    const raw = String(me?.phone_number || me?.PK || '').trim();
                    const digits = raw.replace(/[^\d]/g, '');
                    if (raw.startsWith('+1') || (digits.length === 11 && digits.startsWith('1'))) {
                      const ten = digits.length === 11 ? digits.slice(1) : digits;
                      if (ten.length === 10) {
                        return `+1 (${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6, 10)}`;
                      }
                    }
                    return raw || 'unknown';
                  })()}
                </li>
                <li>
                  <b>Subscription:</b> {plan}
                </li>
                {plan !== 'None' && (
                  <li>
                    <b>{me?.cancel_at_period_end ? 'Ending on:' : 'Renews on:'}</b> {renewalDate}
                  </li>
                )}
              </ul>

              {/* Account setup pending warning */}
              {accountPending && (
                <div
                  style={{
                    marginTop: '16px',
                    padding: '12px 16px',
                    background: 'rgba(251, 191, 36, 0.15)',
                    border: '1px solid rgba(251, 191, 36, 0.4)',
                    borderRadius: '10px',
                    color: '#fcd34d',
                    fontSize: '0.9rem',
                  }}
                >
                  Your account is being set up. This usually takes a few seconds. Please refresh if this persists.
                </div>
              )}

              {/* Subscription setup pending warning */}
              {subscriptionPending && (
                <div
                  style={{
                    marginTop: '16px',
                    padding: '12px 16px',
                    background: 'rgba(251, 191, 36, 0.15)',
                    border: '1px solid rgba(251, 191, 36, 0.4)',
                    borderRadius: '10px',
                    color: '#fcd34d',
                    fontSize: '0.9rem',
                  }}
                >
                  Your subscription is being set up. This usually takes a few seconds. Please refresh if this persists.
                </div>
              )}

              <div className="inline-actions">
                {/* Show Subscribe button if user doesn't have an active subscription */}
                {(plan === 'None' || !me?.subscription_status || me?.subscription_status !== 'active') && (
                  <button className="btn" onClick={handleSubscribe} disabled={subscribing}>
                    {subscribing ? 'Loading…' : 'Subscribe'}
                  </button>
                )}
                <button className="btn" onClick={handleManageBilling} disabled={managingBilling}>
                  {managingBilling ? 'Loading…' : 'Manage billing'}
                </button>
                <a className="btn btn--ghost" href="/yhs-contact.vcf" download>
                  Add YHS to Contacts
                </a>
              </div>

              {billingError && (
                <div
                  style={{
                    marginTop: '16px',
                    padding: '12px 16px',
                    background: 'rgba(248, 113, 113, 0.1)',
                    border: '1px solid rgba(248, 113, 113, 0.3)',
                    borderRadius: '10px',
                    color: '#fca5a5',
                    fontSize: '0.9rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <span>{billingError}</span>
                </div>
              )}
            </>
          ) : (
            <p style={{ opacity: 0.6 }}>Profile unavailable.</p>
          )}
        </section>

        <section className="card">
          <h2>Personalization</h2>
          <form
            className="form-stacked"
            onSubmit={(e) => {
              e.preventDefault();
              handleSave();
            }}
            noValidate
          >
            <label className="field">
              <span className="label">Date of birth</span>
              <DatePicker
                selected={dob ? new Date(dob + 'T00:00:00') : null}
                onChange={(date) => {
                  if (date) {
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    setDob(`${year}-${month}-${day}`);
                  } else {
                    setDob('');
                  }
                }}
                dateFormat="MMMM d, yyyy"
                maxDate={new Date()}
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                placeholderText="Select your date of birth"
                className="input input--date"
                wrapperClassName="datepicker-wrapper"
                aria-label="Date of birth"
                yearDropdownItemNumber={100}
                scrollableYearDropdown
              />
            </label>

            <label className="field">
              <span className="label">Time of birth</span>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.2fr', gap: '8px' }}>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength="2"
                  placeholder="Hour"
                  value={tobHour}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '');
                    const num = parseInt(val, 10);
                    if (val === '' || (num >= 1 && num <= 12)) {
                      setTobHour(val);
                    }
                  }}
                  className="input"
                  aria-label="Hour (1-12)"
                  style={{ fontSize: '16px' }}
                />
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength="2"
                  placeholder="Min"
                  value={tobMinute}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '');
                    const num = parseInt(val, 10);
                    if (val === '' || (num >= 0 && num <= 59)) {
                      setTobMinute(val.padStart(2, '0'));
                    }
                  }}
                  className="input"
                  aria-label="Minute (00-59)"
                  style={{ fontSize: '16px' }}
                />
                <select value={tobPeriod} onChange={(e) => setTobPeriod(e.target.value)} className="input input--select" aria-label="AM or PM" style={{ fontSize: '16px' }}>
                  <option value="AM">AM</option>
                  <option value="PM">PM</option>
                </select>
              </div>
            </label>

            <label className="field">
              <span className="label">Time zone</span>
              <select className="input input--select" value={tz} onChange={(e) => setTz(e.target.value)} aria-label="Time zone">
                <option value="">— Select time zone —</option>
                {tzOptions.map((opt) => (
                  <option key={opt.key} value={opt.key}>
                    {opt.label}
                  </option>
                ))}
              </select>
              {originalTz === 'test' && tz !== 'test' && <small className="hint">Your dev account uses a special "test" timezone.</small>}
            </label>

            {/* Save success banner */}
            {saveSuccess && (
              <div
                style={{
                  padding: '12px 16px',
                  background: 'rgba(34, 197, 94, 0.15)',
                  border: '1px solid rgba(34, 197, 94, 0.4)',
                  borderRadius: '10px',
                  color: '#86efac',
                  fontSize: '0.9rem',
                  marginBottom: '1rem',
                }}
              >
                <span style={{ marginRight: 8 }}>✓</span>
                {saveSuccess}
              </div>
            )}

            {/* Save error banner */}
            {saveError && (
              <div
                style={{
                  padding: '12px 16px',
                  background: 'rgba(248, 113, 113, 0.1)',
                  border: '1px solid rgba(248, 113, 113, 0.3)',
                  borderRadius: '10px',
                  color: '#fca5a5',
                  fontSize: '0.9rem',
                  marginBottom: '1rem',
                }}
              >
                <span style={{ marginRight: 8 }}>✗</span>
                {saveError}
              </div>
            )}

            <div className="row row--start">
              <button className="btn" type="submit" disabled={saving}>
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </form>
        </section>

        {/* Developer section - hidden by default, accessible via footer */}
        {showDeveloperCard && (
          <section className="card" style={{ borderTop: '1px solid rgba(255,255,255,0.05)', marginTop: '3rem', paddingTop: '2rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
              <h3 style={{ fontSize: '0.9rem', opacity: 0.6, margin: 0 }}>Developer</h3>
              <button
                onClick={() => setShowDeveloperCard(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'rgba(255,255,255,0.7)',
                  fontSize: '1.4rem',
                  width: '28px',
                  height: '28px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'color 0.2s ease',
                  padding: 0,
                  lineHeight: '1',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.color = 'rgba(255,255,255,0.9)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.color = 'rgba(255,255,255,0.7)';
                }}
                aria-label="Close Developer section"
                title="Close Developer section"
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              {/* Support toast notification */}
              {supportToast && (
                <div
                  style={{
                    marginBottom: '0.75rem',
                    padding: '12px 16px',
                    background: 'rgba(34, 197, 94, 0.15)',
                    border: '1px solid rgba(34, 197, 94, 0.4)',
                    borderRadius: '10px',
                    color: '#86efac',
                    fontSize: '0.85rem',
                    animation: 'fadeIn 0.3s ease',
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: '4px' }}>Support request sent!</div>
                  <div style={{ opacity: 0.9 }}>We'll get back to you shortly. Sorry for any inconvenience.</div>
                </div>
              )}

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                  gap: '0.5rem',
                  marginBottom: '0.75rem',
                }}
              >
                <button
                  onClick={handleCopyDebugInfo}
                  style={{
                    background: debugCopied ? 'rgba(34, 197, 94, 0.2)' : 'rgba(34, 211, 238, 0.15)',
                    border: `1px solid ${debugCopied ? 'rgba(34, 197, 94, 0.4)' : 'rgba(34, 211, 238, 0.3)'}`,
                    color: debugCopied ? '#86efac' : '#22d3ee',
                    borderRadius: '8px',
                    padding: '10px 14px',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    minHeight: '40px',
                  }}
                  aria-label="Copy debug information"
                >
                  {debugCopied ? 'Copied!' : 'Copy Debug Info'}
                </button>
                <button
                  onClick={handleSendToSupport}
                  style={{
                    background: 'rgba(139, 92, 246, 0.15)',
                    border: '1px solid rgba(139, 92, 246, 0.4)',
                    color: '#a78bfa',
                    borderRadius: '8px',
                    padding: '10px 14px',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    minHeight: '40px',
                  }}
                  aria-label="Send debug info to support"
                >
                  Send to Support
                </button>
              </div>
              <div style={{ fontSize: '0.8rem', lineHeight: 1.6 }}>
                <p style={{ margin: '0.25rem 0' }}>
                  <b>Network Type:</b>{' '}
                  <span style={{ color: getNetworkType(window.location.hostname).startsWith('private-') || getNetworkType(window.location.hostname) === 'localhost' ? '#fbbf24' : '#86efac' }}>
                    {getNetworkType(window.location.hostname)}
                  </span>
                  {(getNetworkType(window.location.hostname).startsWith('private-') || getNetworkType(window.location.hostname) === 'localhost') && (
                    <span style={{ color: '#fbbf24', marginLeft: '6px' }}>(local dev)</span>
                  )}
                </p>
                <p style={{ margin: '0.25rem 0' }}>
                  <b>Auth Token:</b>{' '}
                  <span style={{ color: debugInfo.hasIdToken ? '#86efac' : '#fca5a5' }}>{debugInfo.hasIdToken ? `✓ Present (${debugInfo.idTokenLength} chars)` : '✗ Missing'}</span>
                </p>
                {debugInfo.hasIdToken && (() => {
                  const tokenInfo = getTokenExpirationInfo();
                  return (
                    <p style={{ margin: '0.25rem 0' }}>
                      <b>Token Expires:</b>{' '}
                      <span style={{ color: tokenInfo.isExpired ? '#fca5a5' : tokenInfo.secondsRemaining < 300 ? '#fbbf24' : '#86efac' }}>
                        {tokenInfo.isExpired ? '✗ EXPIRED' : `${Math.round(tokenInfo.secondsRemaining / 60)} min remaining`}
                      </span>
                    </p>
                  );
                })()}
                <p style={{ margin: '0.25rem 0' }}>
                  <b>Online Status:</b>{' '}
                  <span style={{ color: navigator.onLine ? '#86efac' : '#fca5a5' }}>
                    {navigator.onLine ? '✓ Online' : '✗ OFFLINE'}
                  </span>
                </p>
                <p style={{ margin: '0.25rem 0' }}>
                  <b>API Base URL:</b>{' '}
                  <span className="mono" style={{ color: '#cbd5e1' }}>
                    {debugInfo.apiBase || 'not set'}
                  </span>
                </p>
                <p style={{ margin: '0.25rem 0' }}>
                  <b>Request URL:</b>{' '}
                  <span className="mono" style={{ color: '#cbd5e1', fontSize: '0.75rem' }}>
                    {debugInfo.requestUrl || 'N/A'}
                  </span>
                </p>
                <p style={{ margin: '0.25rem 0' }}>
                  <b>Current Page:</b>{' '}
                  <span className="mono" style={{ color: '#cbd5e1', fontSize: '0.75rem' }}>
                    {debugInfo.currentUrl || 'N/A'}
                  </span>
                </p>
                <p style={{ margin: '0.25rem 0' }}>
                  <b>API Response:</b>{' '}
                  <span style={{ color: debugInfo.responseSuccess === true ? '#86efac' : debugInfo.responseSuccess === false ? '#fca5a5' : '#fbbf24' }}>
                    {debugInfo.responseSuccess === true ? '✓ Success' : debugInfo.responseSuccess === false ? '✗ Failed' : '⏳ Pending'}
                  </span>
                </p>
                <p style={{ margin: '0.25rem 0' }}>
                  <b>Screen:</b>{' '}
                  <span style={{ color: '#cbd5e1' }}>
                    {window.innerWidth}×{window.innerHeight} (viewport) @ {window.devicePixelRatio}x
                  </span>
                </p>
                <p style={{ margin: '0.25rem 0' }}>
                  <b>Device:</b>{' '}
                  <span style={{ color: '#cbd5e1' }}>
                    {'ontouchstart' in window || navigator.maxTouchPoints > 0 ? 'Touch' : 'Non-touch'}, {navigator.platform}
                  </span>
                </p>
                {debugInfo.errorMessage && (
                  <p style={{ margin: '0.25rem 0', color: '#fca5a5' }}>
                    <b>Error:</b> {debugInfo.errorMessage}
                  </p>
                )}
                {debugInfo.errorType && (
                  <p style={{ margin: '0.25rem 0', color: '#fca5a5' }}>
                    <b>Error Type:</b> {debugInfo.errorType}
                  </p>
                )}
              </div>
              <details style={{ marginTop: '0.75rem' }}>
                <summary style={{ cursor: 'pointer', fontSize: '0.8rem', opacity: 0.7, marginBottom: '0.75rem' }}>Debug Data</summary>
                <pre className="raw-json" style={{ marginTop: '0.5rem' }}>
                  {JSON.stringify(debugInfo, null, 2)}
                </pre>
              </details>
            </div>

            {me?.stripe_customer_id && (
              <div style={{ marginBottom: '1.5rem' }}>
                <details>
                  <summary style={{ cursor: 'pointer', fontSize: '0.8rem', opacity: 0.7, marginBottom: '0.75rem' }}>Stripe Data</summary>
                  <pre className="raw-json" style={{ lineHeight: 1.6 }}>
                    {`Stripe Customer ID: ${me.stripe_customer_id || 'N/A'}
Subscription Status: ${me.subscription_status || 'N/A'}
Plan: ${me.plan || 'None'}
${
  me.end_date
    ? `${me.cancel_at_period_end ? 'Ends on' : 'Renews on'}: ${new Date(me.end_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`
    : 'No renewal date set'
}
Cancel at Period End: ${me.cancel_at_period_end ? 'Yes' : 'No'}`}
                  </pre>
                </details>
              </div>
            )}

            <div>
              <details>
                <summary style={{ cursor: 'pointer', fontSize: '0.8rem', opacity: 0.7, marginBottom: '0.75rem' }}>User Data</summary>
                <pre className="raw-json">{JSON.stringify(me, null, 2)}</pre>
              </details>
            </div>
          </section>
        )}
      </div>

      <Footer onDeveloperClick={() => setShowDeveloperCard(true)} />
    </main>
  );
}
